// 时间与时区显示工具：后端一律 UTC；本页一律"查看者时区"
import i18n from '../i18n';
import { useSyncExternalStore } from 'react';

const VIEW_TZ_KEY = 'wesc_view_tz';

function listenersInit(): void { /* noop, tz 变化用事件广播 */ }

const tzListeners = new Set<() => void>();

export function getViewTz(): string {
  const saved = localStorage.getItem(VIEW_TZ_KEY);
  if (saved) return saved;
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

export function setViewTz(tz: string): void {
  localStorage.setItem(VIEW_TZ_KEY, tz);
  tzListeners.forEach((f) => f());
}

/** React hook：订阅查看时区（组件重渲染用） */
export function useViewTz(): string {
  return useSyncExternalStore(
    (cb) => {
      tzListeners.add(cb);
      return () => {
        tzListeners.delete(cb);
        listenersInit();
      };
    },
    getViewTz,
    getViewTz,
  );
}

/** 后端时间值（'YYYY-MM-DD HH:mm:ss.SSS' UTC 或 ISO 串）→ Date */
export function toDate(v: string | null | undefined): Date | null {
  if (!v) return null;
  if (v.includes('T')) return new Date(v);
  return new Date(`${v.replace(' ', 'T')}Z`);
}

/** 完整时间显示：2026-09-22 17:26 (Asia/Shanghai 缩写可选) */
export function fmtTime(v: string | null | undefined, tz: string, withZone = false): string {
  const d = toDate(v);
  if (!d) return '—';
  const loc = i18n.language === 'en' ? 'en-GB' : String(i18n.language);
  try {
    const parts = new Intl.DateTimeFormat(loc, {
      timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: withZone ? '2-digit' : undefined, hour12: false,
      ...(withZone ? { timeZoneName: 'short' as const } : {}),
    }).formatToParts(d);
    const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '';
    const date = `${get('year')}-${get('month')}-${get('day')}`;
    const time = `${get('hour')}:${get('minute')}${withZone ? `:${get('second')}` : ''}`;
    const zone = withZone ? ` ${get('timeZoneName')}` : '';
    return `${date} ${time}${zone}`;
  } catch {
    return v ?? '—'; // 无效时区名时原样返回
  }
}

/** 相对时间（列表紧凑展示用）：3分钟前 / 2h ago */
export function fmtAgo(v: string | null | undefined, now = new Date()): string {
  const d = toDate(v);
  if (!d) return '—';
  const diff = Math.round((now.getTime() - d.getTime()) / 1000);
  const loc = i18n.language;
  const zh = String(loc).startsWith('zh');
  if (diff < 60) return zh ? `${Math.max(0, diff)} 秒前` : `${Math.max(0, diff)}s ago`;
  if (diff < 3600) return zh ? `${Math.floor(diff / 60)} 分钟前` : `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return zh ? `${Math.floor(diff / 3600)} 小时前` : `${Math.floor(diff / 3600)}h ago`;
  return zh ? `${Math.floor(diff / 86400)} 天前` : `${Math.floor(diff / 86400)}d ago`;
}

/** 秒 → "1d 2h 3m" / "45s"（等宽仪表风） */
export function fmtDuration(sec: number | string | null | undefined): string {
  if (sec == null || sec === '') return '—';
  let s = Math.max(0, Number(sec));
  if (!Number.isFinite(s)) return '—';
  if (s < 60) return `${Math.round(s)}s`;
  const d = Math.floor(s / 86400);
  s -= d * 86400;
  const h = Math.floor(s / 3600);
  s -= h * 3600;
  const m = Math.floor(s / 60);
  return [d ? `${d}d` : '', h ? `${h}h` : '', `${m}m`].filter(Boolean).join(' ');
}

/** 常用 IANA 时区（覆盖团队所在地区 + 主要金融/云区域） */
export const TZ_PRESETS = [
  'UTC',
  'Asia/Shanghai',
  'Asia/Hong_Kong',
  'Asia/Taipei',
  'Asia/Singapore',
  'Asia/Tokyo',
  'Asia/Seoul',
  'Asia/Kolkata',
  'Asia/Dubai',
  'Europe/London',
  'Europe/Frankfurt',
  'Australia/Sydney',
  'America/New_York',
  'America/Chicago',
  'America/Los_Angeles',
  'America/Sao_Paulo',
].filter((tz) => {
  try {
    new Intl.DateTimeFormat('en', { timeZone: tz }).format(new Date());
    return true;
  } catch {
    return false;
  }
});
